'use client'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import SelectMUI, { SelectProps as ChildSelectProps, SelectChangeEvent } from '@mui/material/Select'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { EmptyContent } from '@/components/common/empty-content'
import { Box, InputAdornment, useMediaQuery, useTheme } from '@mui/material'
import { Controller, FieldValues, Path, useFormContext, useFormState } from 'react-hook-form'
import { useRef } from 'react'
import { ErrorTooltipIcon, type ErrorTooltipIconRef, FieldLabel } from '../rhf-text-field/components'
import { getErrorBorderStyles } from '../rhf-text-field/styles'
interface SelectProps<T extends FieldValues> {
  className?: string
  placeholder?: String
  options?: any
  label?: string | React.ReactNode
  required?: boolean
  name: Path<T>
  helperText?: React.ReactNode
  defaultValue?: {
    label?: any
    value: any
  }
  onChange?: (e: SelectChangeEvent) => void
  selectProps?: Omit<ChildSelectProps, 'children'>
}
const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  disableScrollLock: true,
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

export const RHFSelect = <T extends FieldValues>({
  options,
  placeholder,
  className,
  label,
  required,
  name,
  helperText,
  ...props
}: SelectProps<T>) => {
  const { control } = useFormContext()
  const { isSubmitted } = useFormState({ control })
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const errorIconRef = useRef<ErrorTooltipIconRef>(null)

  const handleSelectClick = () => {
    // Ẩn tooltip khi click vào select
    if (errorIconRef.current) {
      errorIconRef.current.hideTooltip()
    }
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {label && <FieldLabel label={label} required={required} />}
      <Controller
        name={name}
        control={control}
        defaultValue={props?.defaultValue?.value ?? ''}
        render={({ field: { value, onChange, ...field }, fieldState: { error } }) => {
          return (
            <FormControl error={!!error} className="w-full">
              <SelectMUI
                {...field}
                value={value ?? ''}
                labelId={name}
                id={name}
                className={`w-full ${className}`}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  onChange(e)
                  props?.onChange?.(e as any)
                }}
                onClick={handleSelectClick}
                displayEmpty
                MenuProps={MenuProps}
                IconComponent={(iconProps) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    {/* Error Icon với MUI Tooltip */}
                    {error && (
                      <ErrorTooltipIcon
                        ref={errorIconRef}
                        errorMessage={error.message || ''}
                        showTooltip={isDesktop}
                        isSubmitted={isSubmitted}
                      />
                    )}
                    {/* Dropdown Icon */}
                    <ExpandMoreIcon {...iconProps} />
                  </Box>
                )}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span className="opacity-50">{placeholder}</span>
                  }
                  return options?.find((el: any) => el?.value == selected)?.label
                }}
                // Show helperText only on mobile
                {...(error &&
                  !isDesktop && {
                    error: true,
                  })}
                sx={
                  {
                    ...props?.selectProps?.sx,
                    ...getErrorBorderStyles(!!error),
                  } as any
                }
                {...props?.selectProps}
              >
                {options?.map((val: any) => (
                  <MenuItem value={val?.value} key={val?.value}>
                    {val.label}
                  </MenuItem>
                ))}
                {/* empty optiom */}
                {!options?.length && <EmptyContent filled title="" />}
              </SelectMUI>
              {/* Show helper text on mobile only */}
              {!isDesktop && (error?.message || helperText) && (
                <Box
                  sx={{
                    mt: 0.5,
                    mx: 1.75,
                    fontSize: '0.75rem',
                    color: error ? 'error.main' : 'text.secondary',
                  }}
                >
                  {error?.message || helperText}
                </Box>
              )}
            </FormControl>
          )
        }}
      />
    </Box>
  )
}
